import PropTypes from 'prop-types';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
// import Link from 'next/link';

import { useTheme } from '@mui/material';

import { DEFAULT } from 'components/Scroller/constants';
import { CruiseSearchContext } from 'contexts/CruiseSearchContext';
import { CABIN_CLASS, INTERIOR, SAILDATE } from 'contexts/CruiseSearchContext/constants';
import { CruiseSelectionContext } from 'contexts/CruiseSelectionContext';
import { COMPARE_PAGE_BASE_PATH, MIN_CRUISES_AMMOUNT, SELECTING } from 'contexts/CruiseSelectionContext/constants';

import CompareCruisesList from 'components/CompareCruisesList';
import { ListCurrency } from 'components/Globalization';
import Dropdown from 'components/buttons/Dropdown';
import { getBrandUISettings } from 'lib/utilities/Brands';

import {
	CompareButtonsContainer,
	CompareCloseButton,
	CompareCruisesContainer,
	DropdownsRow,
	GoToCompareButton,
	Label,
	LabeledDropdown,
	ResultsHeaderWrap,
	SectionTitle,
	TitleRow,
} from './styles';

import { RESULTS_DROPDOWN_ROOM, RESULTS_DROPDOWN_SORT } from '../constants';

import { getSelectedSortLabel } from './utils';

import classnames from 'classnames';
import { animateOut } from 'components/SearchPage/animations';
import { utagGoToCompare } from 'contexts/AnalyticsContext/functions';
import { getCompareUrl } from 'contexts/CruiseSelectionContext/utils';
import { useRouter } from 'next/router';
import { useFeatureFlags } from '../../../hooks/LaunchDarkly/useFeatureFlags';

const RECOMMENDED_SORT_OPTION_CODE = 'RECOMMENDED';

/**
 * Displays section header and dropdown filters for SearchCard results.
 * Also displays the compare selecting state, which includes cruise titles, open/close compare button, and button to navigate to the compare page.
 *
 * @param {object} passedRefs - Used for animations
 */

function ResultsHeader({ passedRefs, cruises }) {
	const router = useRouter();
	const { palette } = useTheme();
	const { comparisonViewState, cruisesComparing, resetCruisesComparing, setComparisonViewClosed, maxCountForSelection } =
		useContext(CruiseSelectionContext);

	const {
		results,
		updateSearchSelectingFilter,
		applyFilters,
		searchApplied,
		sortApplied,
		defaultSorting,
		updateSortApplied,
		filterOptionsDictionary,
		globalization,
		brand,
		locale,
	} = useContext(CruiseSearchContext);
	const { enableRecommendedSortOption, enableDateOption, showRoomTypeFilter } = useFeatureFlags();

	const timeline = useRef();

	const { uiSettings } = getBrandUISettings(brand);
	const { cabinClassOptions, generalDictionary } = globalization?.dictionaries || {};

	const {
		actionSortOptionRecommended,
		actionSortOptionDurationLowHigh,
		actionSortOptionDurationHighLow,
		actionSortOptionPriceLowHigh,
		actionSortOptionPriceHighLow,
		labelAllCruiseSearchResults,
		labelCruiseSearchResults,
		labelRoomType,
		labelSortBy,
		actionSortOptionDate,
	} = generalDictionary;

	const labels = {
		DURATION_LOW_TO_HIGH: actionSortOptionDurationLowHigh,
		DURATION_HIGH_TO_LOW: actionSortOptionDurationHighLow,
		PRICE_LOW_TO_HIGH: actionSortOptionPriceLowHigh,
		PRICE_HIGH_TO_LOW: actionSortOptionPriceHighLow,
		RECOMMENDED: actionSortOptionRecommended,
		SAIL_DATE: actionSortOptionDate,
	};

	const { compareDictionary } = globalization?.dictionaries || {};
	const { actionCompare, labelCompareAppender, labelCompareCruisesNumber, actionCompareModeCancel } = compareDictionary;

	const goToCompareClasses = classnames({
		disabled: cruisesComparing?.length <= 1,
	});

	const goToCompare = () => {
		const compareHref = getCompareUrl(COMPARE_PAGE_BASE_PATH, router.query, cruisesComparing);

		utagGoToCompare(cruises, cruisesComparing, locale, brand);

		router.prefetch(compareHref);
		const onComplete = () => {
			resetCruisesComparing();
		};
		window.location.href = compareHref;
		animateOut({
			timeline,
			refs: passedRefs,
			onComplete,
		});
	};

	const [sortOptions, setSortOptions] = useState(
		filterOptionsDictionary?.sort?.options
			?.map((item) => ({
				...item,
				label: labels[`${item.code}`],
			}))
			.sort((a, b) => (b.label > a.label ? 1 : -1)),
	);

	useEffect(() => {
		if (!enableRecommendedSortOption) {
			setSortOptions((prev) => prev.filter((item) => item.code !== RECOMMENDED_SORT_OPTION_CODE));
		}
	}, [enableRecommendedSortOption]);

	const cruisesCount = results?.total;
	const selectingForCompare = comparisonViewState === SELECTING;

	const sortDropdownLabel = labelSortBy;
	const cabinClassDropdownLabel = labelRoomType;
	const comparePageTitle = useMemo(() => labelCompareCruisesNumber.replace('{{value}}', maxCountForSelection), [maxCountForSelection, labelCompareCruisesNumber]);

	let sectionTitle = '';

	if (selectingForCompare) {
		sectionTitle = comparePageTitle;
	} else {
		sectionTitle = (
			<>
				<h1>{results?.openSearch ? labelAllCruiseSearchResults : labelCruiseSearchResults}</h1> {!!cruisesCount && <span>({cruisesCount})</span>}
			</>
		);
	}

	const cabinList = [
		...cabinClassOptions.map((cabin) => {
			delete cabin['disabled'];
			return cabin;
		}),
	];

	function getCabinClassLabel(cabinClass) {
		const label = cabinList.find((cls) => cls?.id === cabinClass)?.label;
		return label ? label.toLowerCase() : '';
	}

	function filterResultsByCabinClass(cabinClass) {
		updateSearchSelectingFilter(CABIN_CLASS, [cabinClass?.id], applyFilters);
	}

	const labelCabin = (searchApplied?.[CABIN_CLASS]?.[0] || '').toLowerCase();
	const cabinClassLabel = labelCabin || getCabinClassLabel(INTERIOR);

	const sortLabel = getSelectedSortLabel(sortApplied, defaultSorting, sortOptions);
	const onSortChange = useCallback(
		(option) => {
			const { attribute, order } = option;
			if (!attribute) return;

			const sort = [attribute, order].filter(Boolean);
			updateSortApplied(sort, option.label);
		},
		[updateSortApplied],
	);

	const closeComparisonOnClick = () => {
		setComparisonViewClosed();
		resetCruisesComparing();
	};

	const filterSortOptions = enableDateOption ? sortOptions : sortOptions?.filter((val) => val.attribute != SAILDATE);

	return (
		<ResultsHeaderWrap data-testid="results-header-container" ref={passedRefs?.current?.headerRef} className={selectingForCompare ? 'compare' : ''}>
			<TitleRow data-testid="number-results-label">
				<SectionTitle
					className={selectingForCompare ? 'compare' : ''}
					component="div"
					variant="title2"
					palette={palette}
					styles={uiSettings.resultsHeader.title}
				>
					<span>{sectionTitle}</span>
				</SectionTitle>
			</TitleRow>

			{!selectingForCompare && (
				<DropdownsRow>
					<ListCurrency />
					<LabeledDropdown id={RESULTS_DROPDOWN_SORT}>
						<Label component="p" variant="title2" palette={palette} styles={uiSettings.resultsHeader.labels}>
							<span>{sortDropdownLabel}:</span>
						</Label>
						<Dropdown
							dataTestId="sort-by-results-dropdown"
							label={sortLabel}
							onChange={onSortChange}
							options={filterSortOptions}
							scrollerId={DEFAULT}
							buttonStyleText
							select
						/>
					</LabeledDropdown>
					{showRoomTypeFilter && (
						<>
							<LabeledDropdown id={RESULTS_DROPDOWN_ROOM}>
								<Label>{cabinClassDropdownLabel}:</Label>
								<Dropdown
									dataTestId="sort-by-results-dropdown"
									label={cabinClassLabel}
									onChange={filterResultsByCabinClass}
									options={cabinList}
									scrollerId={DEFAULT}
									buttonStyleText
									select
								/>
							</LabeledDropdown>
						</>
					)}
				</DropdownsRow>
			)}

			{selectingForCompare && (
				<CompareCruisesContainer>
					<CompareCruisesList />
					<CompareButtonsContainer>
						<CompareCloseButton onClick={closeComparisonOnClick} variant="text">
							{actionCompareModeCancel}
						</CompareCloseButton>
						{cruisesComparing.length >= MIN_CRUISES_AMMOUNT && (
							<GoToCompareButton id="go-to-compare-btn" primary className={goToCompareClasses} onClick={goToCompare} variant="contained" size="large">
								{actionCompare + ' ' + cruisesComparing.length + ' ' + labelCompareAppender}
							</GoToCompareButton>
						)}
					</CompareButtonsContainer>
				</CompareCruisesContainer>
			)}
		</ResultsHeaderWrap>
	);
}

ResultsHeader.propTypes = {
	passedRefs: PropTypes.object.isRequired,
	cruises: PropTypes.array.isRequired,
};

export default ResultsHeader;
